import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import noticeStyles from '../../css/admin/NoticeBoard.module.css';
import styles from '../../css/admin/SettlementStatement.module.css';
import { isApp } from '../../utils/isApp'; // [APP-FORK]
import {
  MESSAGES,
  formatCount,
  formatMonthLabel,
  formatWon,
  isValidPayoutMonth,
  mobileListPath,
} from './settlementStatement.util';
import useSettlementStatement from './useSettlementStatement';

function SettlementRow({ row }) {
  if (row.isSub) {
    return (
      <div className={`${styles.row} ${styles.rowSub}`}>
        <span className={styles.rowLabel}>{row.label}</span>
        <span>{formatWon(row.amount)}</span>
        <span className={styles.rowCount}>{formatCount(row.count)}</span>
      </div>
    );
  }
  return (
    <div className={styles.row}>
      <span
        className={`${styles.sign} ${row.sign === '+' ? styles.signPlus : styles.signMinus}`}
        aria-label={row.sign === '+' ? '더하기' : '빼기'}
      >
        {row.sign}
      </span>
      <span className={styles.rowLabel}>{row.label}</span>
      <span className={styles.rowAmount}>{formatWon(row.amount)}</span>
      <span className={styles.rowCount}>{formatCount(row.count)}</span>
    </div>
  );
}

function StatementBody({ view }) {
  return (
    <>
      <section className={noticeStyles.card} aria-label="요약">
        <div className={styles.summaryLabel}>지급예정 금액</div>
        <div className={styles.summaryAmount}>{formatWon(view.payoutAmount)}</div>
        <div className={styles.dueChip}>{view.payoutDue} 지급</div>
        <hr className={styles.divider} />
        <dl className={styles.infoList}>
          <dt>상점명</dt>
          <dd>
            {view.storeName}
            {view.adminId && <span className={styles.infoSub}> ({view.adminId})</span>}
          </dd>
          <dt>이용기간</dt>
          <dd>{view.usagePeriod}</dd>
        </dl>
      </section>

      <section className={noticeStyles.card}>
        <h3 className={styles.cardTitle}>정산 내역</h3>
        {view.settlementRows.map(row => (
          <SettlementRow key={row.key} row={row} />
        ))}
        <div className={styles.totalBox}>
          <span className={styles.totalText}>
            <span className={styles.totalLabel}>지급예정 금액</span>
            <span className={styles.totalFormula}>{view.formula}</span>
          </span>
          <span className={styles.totalAmount}>{formatWon(view.payoutAmount)}</span>
        </div>
      </section>
    </>
  );
}

/** 정산명세서 상세(/mobile/:storeNo/settlement/:payoutMonth). 주소의 월은 정산월이다. */
export default function SettlementDetailMobile() {
  const { storeNo, payoutMonth } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const validMonth = isValidPayoutMonth(payoutMonth);
  const result = useSettlementStatement(payoutMonth, validMonth);

  useEffect(() => {
    if (!validMonth) navigate(mobileListPath(storeNo), { replace: true });
  }, [navigate, storeNo, validMonth]);

  if (!validMonth) return null;

  const title = `${formatMonthLabel(payoutMonth)} 정산명세서`;

  // 목록에서 들어왔으면 뒤로, 링크로 바로 들어왔으면 목록으로 대체한다.
  const onBack = () => {
    if (location.state?.fromList) navigate(-1);
    else navigate(mobileListPath(storeNo), { replace: true });
  };

  return (
    <div
      className={`${noticeStyles.page} ${noticeStyles.noticePage}${
        isApp() ? ` ${noticeStyles.pageApp}` : ''
      }`}
    >
      <section className={noticeStyles.section}>
        <div className={noticeStyles.header}>
          <button
            type="button"
            className={noticeStyles.backButton}
            onClick={onBack}
            aria-label="목록으로"
          >
            ‹
          </button>
          <h1 className={noticeStyles.pageTitle}>{title}</h1>
        </div>

        {result.status === 'loading' && (
          <p className={styles.message}>{MESSAGES.loading}</p>
        )}
        {result.status === 'empty' && <p className={styles.message}>{MESSAGES.empty}</p>}
        {result.status === 'message' && <p className={styles.message}>{result.message}</p>}
        {result.status === 'ready' && <StatementBody view={result.view} />}
      </section>
    </div>
  );
}
